import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {isWithinInterval, isEqual, isAfter, isBefore, isDate} from 'date-fns'
import {lighten, makeStyles} from '@material-ui/core/styles'
import {
    Box,
    Button,
    Chip,
    Checkbox,
    IconButton,
    InputBase,
    Typography,
    Toolbar,
    Tooltip,
    Paper,
} from '@material-ui/core'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TablePagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import TableSortLabel from '@material-ui/core/TableSortLabel'
//import Tooltip from '@material-ui/core/Tooltip'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import OccurrenceDrawer from './OccurrenceDrawer'
import OccurrenceTableFilter from './OccurrenceTableFilter'
import ErrorBoundary from '../../../../components/ErrorBoundary'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import LinearProgress from '@material-ui/core/LinearProgress'
import OccurrenceMoreMenu from './OccurrenceMoreMenu'

//******** Images *************************
import SearchIcon from '@material-ui/icons/Search'
import DeleteIcon from '@material-ui/icons/Delete'
import VirtualEventIcon from '@material-ui/icons/Language'
import Close from '@material-ui/icons/Close'
import {ReactComponent as AddEventOccurrence} from '../../../../images/calendar-add-nopadding.svg'
import {ReactComponent as Leftburst} from '../../../../images/leftburst.svg'
import {ReactComponent as Rightburst} from '../../../../images/rightburst.svg'

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1
    }
    if (b[orderBy] > a[orderBy]) {
        return 1
    }
    return 0
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index])
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0])
        if (order !== 0) return order
        return a[1] - b[1]
    })
    return stabilizedThis.map(el => el[0])
}

const headCells = [
    {
        id: 'dateStart',
        numeric: false,
        disablePadding: true,
        label: 'START DATE',
    },
    {
        id: 'occurTitle',
        numeric: false,
        disablePadding: false,
        label: 'EVENT OCCURRENCE TITLE',
    },
    {id: 'swipeCount', numeric: true, disablePadding: false, label: 'COUNT'},
    {id: 'status', numeric: true, disablePadding: false, label: 'STATUS'},
]

function EnhancedTableHead(props) {
    const {
        classes,
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
        disabledArchive,
    } = props
    const createSortHandler = property => event => {
        onRequestSort(event, property)
    }

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        indeterminate={
                            numSelected > 0 && numSelected < rowCount
                        }
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{'aria-label': 'select all occurences'}}
                        disabled={disabledArchive}
                    />
                </TableCell>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'center' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell> </TableCell>
            </TableRow>
        </TableHead>
    )
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
}

const useToolbarStyles = makeStyles(theme => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    title: {
        flex: '1 1 100%',
    },

    buttonlabel: {
        width: '320px',
        whiteSpace: 'nowrap',
    },
    filterlabel: {
        marginTop: '-5px',
    },
    searchBox: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        border: '1px solid',
        borderColor: theme.palette.grey.medium,
        borderRadius: theme.shape.borderRadius,
        margin: '0 0 0 5px',
        height: '40px',
    },
    searchInput: {
        flex: 1,
        width: '150px',
        margin: '0 10px',
    },
    clearButton: {
        padding: 8,
        color: theme.palette.grey.medium,
    },
}))

const EnhancedTableToolbar = props => {
    const classes = useToolbarStyles()
    const {
        numSelected,
        currenteventcategory,
        setRow,
        searchTerm,
        setSearchTerm,
        handleClickOpen,
        handleDelete,
        disabledArchive,
        selectedDate,
        filterswiping,
        handleDateChange,
        setFilterSwiping,
    } = props

    const handleChange = e => {
        setSearchTerm(e.target.value)
    }

    const handleClearSearch = () => {
        setSearchTerm('')
    }

    return (
        <Toolbar
            className={clsx(classes.root, {
                [classes.highlight]: numSelected > 0,
            })}
        >
            {numSelected > 0 ? (
                <Typography
                    className={classes.title}
                    color="inherit"
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    className={classes.title}
                    align="left"
                    variant="h4"
                    id="tableTitle"
                    component="div"
                >
                    {currenteventcategory.name}
                </Typography>
            )}

            {numSelected > 0 ? (
                <Tooltip title="Delete">
                    <IconButton aria-label="delete" onClick={handleDelete}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <>
                    <Button
                        variant="contained"
                        color="primary"
                        aria-label="create new event"
                        onClick={() => {
                            setRow({})
                            handleClickOpen('Create Event Occurrence')
                        }}
                        size="medium"
                        startIcon={
                            <AddEventOccurrence
                                style={{
                                    fill: '#ffffff',
                                    height: '25px',
                                    padding: '0 10px 0 0',
                                }}
                            />
                        }
                        style={{
                            margin: '0 5px',
                            height: '40px',
                            boxShadow: 'none',
                        }}
                        classes={{
                            label: classes.buttonlabel,
                        }}
                        disabled={disabledArchive}
                    >
                        Create Event
                    </Button>

                    <OccurrenceTableFilter
                        selectedDate={selectedDate}
                        filterswiping={filterswiping}
                        handleDateChange={handleDateChange}
                        setFilterSwiping={setFilterSwiping}
                    />

                    <Box className={classes.searchBox}>
                        <InputBase
                            className={classes.searchInput}
                            placeholder="Search"
                            inputProps={{
                                'aria-label': 'search events',
                            }}
                            value={searchTerm}
                            onChange={handleChange}
                        />
                        {searchTerm && (
                            <IconButton
                                className={classes.clearButton}
                                onClick={() => handleClearSearch()}
                            >
                                {' '}
                                <Close />{' '}
                            </IconButton>
                        )}
                        {!searchTerm && (
                            <SearchIcon style={{margin: '0 5px'}} />
                        )}
                    </Box>
                </>
            )}
        </Toolbar>
    )
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
}

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    table: {
        minWidth: 750,
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },
    filtertagBox: {
        display: 'flex',
        flexDirection: 'row',
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
    },
    counterhighlightBox: {
        display: 'flex',
        flexDirection: 'row',
    },
}))

export default function OccurrenceTable(props) {
    const classes = useStyles()
    const [order, setOrder] = React.useState('desc')
    const [orderBy, setOrderBy] = React.useState('dateStart')
    const [selected, setSelected] = React.useState([])
    const [page, setPage] = React.useState(0)
    const [dense, setDense] = React.useState(true)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [openDialog, setOpenDialog] = React.useState(false)
    const [title, setTitle] = React.useState(' ')
    const [row, setRow] = React.useState({})
    const [selectedDate, handleDateChange] = useState([null, null])
    const [filterswiping, setFilterSwiping] = useState('Show All')

    const {
        token,
        currenteventcategory,
        eventoccurrences,
        eventoccurDelete,
    } = props

    const zeroswipeRowCount = eventoccurrences.filter(
        item => item.swipeCount === 0
    )

    const disabledArchive = currenteventcategory.status === 'I' ? true : false
    const filtersActive =
        filterswiping !== 'Show All' ||
        !selectedDate.every(element => element === null)

    // Dialog parameters
    const [open, setOpen] = useState(false)

    const handleOpenDeleteDialog = () => {
        //setRow(superadmin)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }
    // end - dialog parameters

    const handleDelete = () => {
        let payload = {eventoccurrences: selected}
        eventoccurDelete(token, payload)
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            const newSelecteds = eventoccurrences.filter(
                item => item.swipeCount === 0
            )
            setSelected(newSelecteds)
            return
        }
        setSelected([])
    }

    const handleClick = (event, row) => {
        const selectedIndex = selected
            .map(element => element.occurId)
            .indexOf(row.occurId)

        let newSelected = []
        if (row.swipeCount === 0) {
            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, row)
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1))
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1))
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1)
                )
            }

            setSelected(newSelected)
        }
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const handleChangeDense = event => {
        setDense(event.target.checked)
    }

    const handleUpload = eventoccurrence => {
        setRow(eventoccurrence)
        handleClickOpen('Batch Record Swipes')
    }

    const handleCloneOccurrence = eventoccurrence => {
        setRow(eventoccurrence)
        handleClickOpen('Clone Event Occurrence')
    }

    const handleUpdateOccurrence = eventoccurrence => {
        setRow(eventoccurrence)
        handleClickOpen('Edit Event Occurrence')
    }

    const handleClickOpen = title => {
        setOpenDialog(true)
        setTitle(title)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
    }

    const isSelected = occurId =>
        selected.map(element => element.occurId).indexOf(occurId) !== -1

    const emptyRows =
        rowsPerPage -
        Math.min(rowsPerPage, eventoccurrences.length - page * rowsPerPage)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState(eventoccurrences)

    useEffect(() => {
        const results1 = eventoccurrences.filter(event =>
            event.occurTitle.toLowerCase().includes(searchTerm.toLowerCase())
        )

        const results2 = results1.filter(event =>
            filterswiping === 'Active'
                ? isAfter(new Date(event.dateEnd), new Date(event.dateStart)) ||
                  isEqual(new Date(event.dateStart), new Date(event.dateEnd))
                    ? isWithinInterval(new Date(), {
                          start: new Date(event.dateStart),
                          end: new Date(event.dateEnd),
                      })
                        ? true
                        : false
                    : false
                : filterswiping === 'Inactive'
                ? isAfter(new Date(event.dateEnd), new Date(event.dateStart)) ||
                  isEqual(new Date(event.dateStart), new Date(event.dateEnd))
                    ? !isWithinInterval(new Date(), {
                          start: new Date(event.dateStart),
                          end: new Date(event.dateEnd),
                      })
                        ? true
                        : false
                    : false
                : true
        )

        const results3 =
            selectedDate[0] == null && selectedDate[1] == null
                ? results2
                : results2.filter(event =>
                      selectedDate[1] == null
                          ? isAfter(
                                new Date(event.dateStart),
                                new Date(selectedDate[0])
                            ) ||
                            isEqual(
                                new Date(event.dateStart),
                                new Date(selectedDate[0])
                            )
                              ? true
                              : false
                          : selectedDate[0] == null
                          ? isBefore(
                                new Date(event.dateEnd),
                                new Date(selectedDate[1])
                            ) ||
                            isEqual(
                                new Date(event.dateEnd),
                                new Date(selectedDate[1])
                            )
                              ? true
                              : false
                          : isDate(selectedDate[0]) &&
                            isAfter(
                                new Date(selectedDate[1]),
                                new Date(selectedDate[0])
                            )
                          ? isWithinInterval(new Date(event.dateStart), {
                                start: new Date(selectedDate[0]),
                                end: new Date(selectedDate[1]),
                            }) ||
                            isWithinInterval(new Date(event.dateEnd), {
                                start: new Date(selectedDate[0]),
                                end: new Date(selectedDate[1]),
                            })
                              ? true
                              : false
                          : false
                  )

        setSearchResults(results3)
    }, [eventoccurrences, searchTerm, filterswiping, selectedDate])

    return (
        <div className={classes.root}>
            <Paper className={classes.paper}>
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    currenteventcategory={currenteventcategory}
                    setRow={setRow}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    handleClickOpen={handleClickOpen}
                    handleDelete={handleOpenDeleteDialog}
                    disabledArchive={disabledArchive}
                    selectedDate={selectedDate}
                    filterswiping={filterswiping}
                    handleDateChange={handleDateChange}
                    setFilterSwiping={setFilterSwiping}
                />
                {filtersActive ? (
                    <Box className={classes.filtertagBox}>
                        <Typography
                            align="left"
                            variant="body2"
                            id="filtertags"
                            style={{padding: '0 5px'}}
                        >
                            Filtered by:
                        </Typography>

                        {!selectedDate.every(element => element === null) ? (
                            <Chip
                                size="small"
                                label="Date range"
                                onDelete={() => handleDateChange([null, null])}
                                color="primary"
                                style={{margin: '0 5px'}}
                            />
                        ) : null}
                        {filterswiping !== 'Show All' ? (
                            <Chip
                                size="small"
                                label="Swiping status"
                                onDelete={() => setFilterSwiping('Show All')}
                                color="secondary"
                                style={{margin: '0 5px'}}
                            />
                        ) : null}
                    </Box>
                ) : null}
                <TableContainer>
                    <Table
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={zeroswipeRowCount.length}
                            disabledArchive={disabledArchive}
                        />
                        <TableBody>
                            {stableSort(
                                searchResults,
                                getComparator(order, orderBy)
                            )
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((row, index) => {
                                    const isItemSelected = isSelected(
                                        row.occurId
                                    )
                                    const labelId = `occurrences-table-checkbox-${index}`
                                    const newDate = new Date(
                                        row.dateStart
                                    ).toDateString()
                                    const swipeperiodstatus =
                                        row.dateStart &&
                                        (isAfter(
                                            new Date(row.dateEnd),
                                            new Date(row.dateStart)
                                        ) ||
                                            isEqual(
                                                new Date(row.dateEnd),
                                                new Date(row.dateStart)
                                            ))
                                            ? isWithinInterval(new Date(), {
                                                  start: new Date(
                                                      row.dateStart
                                                  ),
                                                  end: new Date(row.dateEnd),
                                              })
                                                ? 'A'
                                                : 'I'
                                            : 'I'
                                    const countChanged =
                                        'prevswipeCount' in row
                                            ? row.prevswipeCount !==
                                              row.swipeCount
                                            : false
                                    const fetchingSwipeResult = row.loadingRecordSwipeResults
                                        ? row.loadingRecordSwipeResults
                                        : false
                                    const isVirtual =
                                        ['H', 'O'].indexOf(row.occurKind) >= 0
                                            ? true
                                            : false
                                    return (
                                        <TableRow
                                            hover
                                            onClick={() => {
                                                handleUpdateOccurrence(row)
                                            }}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.occurId}
                                            selected={isItemSelected}
                                        >
                                            <TableCell
                                                padding="checkbox"
                                                onClick={event => {
                                                    if (!disabledArchive) {
                                                        event.stopPropagation()
                                                        handleClick(event, row)
                                                    }
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isItemSelected}
                                                    inputProps={{
                                                        'aria-labelledby': labelId,
                                                    }}
                                                    disabled={
                                                        disabledArchive ||
                                                        row.swipeCount > 0
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                {newDate}
                                            </TableCell>
                                            <TableCell align="left">
                                                {isVirtual && (
                                                    <VirtualEventIcon
                                                        style={{
                                                            fontSize: '0.84rem',
                                                        }}
                                                    />
                                                )}{' '}
                                                {row.occurTitle}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={{minWidth: '200px'}}
                                            >
                                                {fetchingSwipeResult ? (
                                                    <LinearProgress
                                                        style={{
                                                            color: '#00853e',
                                                        }}
                                                        thickness={1}
                                                    />
                                                ) : (
                                                    <>
                                                        {countChanged ? (
                                                            <Leftburst
                                                                style={{
                                                                    height:
                                                                        '20px',
                                                                }}
                                                            />
                                                        ) : null}
                                                        {row.swipeCount}
                                                        {countChanged ? (
                                                            <Rightburst
                                                                style={{
                                                                    height:
                                                                        '20px',
                                                                }}
                                                            />
                                                        ) : null}
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <div
                                                    style={{
                                                        backgroundColor:
                                                            swipeperiodstatus ===
                                                            'A'
                                                                ? '#098447'
                                                                : '#707070',
                                                        borderRadius: '20px',
                                                        color: '#ffffff',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {swipeperiodstatus === 'A'
                                                        ? 'Active'
                                                        : 'Inactive'}
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                }}
                                            >
                                                <OccurrenceMoreMenu
                                                    handleUpload={handleUpload}
                                                    handleCloneOccurrence={
                                                        handleCloneOccurrence
                                                    }
                                                    row={row}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (dense ? 33 : 53) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={eventoccurrences.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
            <FormControlLabel
                control={
                    <Switch checked={dense} onChange={handleChangeDense} />
                }
                label="Dense padding"
            />

            <OccurrenceDrawer
                title={title}
                currenteventcategory={currenteventcategory}
                eventoccurrence={row}
                openDialog={openDialog}
                handleCloseDialog={handleCloseDialog}
            />

            <ErrorBoundary>
                <Dialog
                    aria-labelledby="Confirm deletion of event occurrences"
                    aria-describedby="Delete Event Occurrence Modal"
                    open={open}
                    scroll="paper"
                    onClose={handleClose}
                >
                    <DialogTitle id="confirmation-delete-event-occurrence">
                        Delete Event Occurrences?
                    </DialogTitle>
                    <DialogContent>
                        {selected.map(item => item.occurTitle + ' ') +
                            ' will be removed. Continue?'}
                    </DialogContent>
                    <DialogActions>
                        <Button autoFocus onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDelete} color="primary">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </ErrorBoundary>
        </div>
    )
}
